import classNames from 'classnames';
import { createRef, useEffect, useState } from 'react';
import { ScrapReviewResult, ScrapReasons, ScrapReason } from '../../shared/types/operation.ts';
import { Operation } from '../types/sharedTypeImpl.ts';
import { reportError } from '../utils/errorReporting.ts';
import { trpc } from '../utils/trpc/trpc.ts';
import { ModalSubmitDialog } from './modalSubmitDialog.tsx';
import { OperatorSelect } from './operatorSelect.tsx';
import { ProtocolPreviewDialog } from './protocol-preview-dialog/protocolPreviewDialog.tsx';
import { RadioGroup } from './radioGroup.tsx';


interface Props {
    isOpen: boolean;
    operation: Operation;
    onClose: () => void;
    onSuccess: (operation: Operation) => void;
}

export function ScrapReviewDialog({
    isOpen,
    operation,
    onClose,
    onSuccess
}: Props) {
    const formRef = createRef<HTMLFormElement>();
    const [showPdfDialog, setShowPdfDialog] = useState(false);
    const [result, setResult] = useState<ScrapReviewResult | null>(null);
    const [comment, setComment] = useState('');
    const [operator, setOperator] = useState('');
    const [scrapReason, setScrapReason] = useState<ScrapReason | null>(null);

    const operationUpdate = trpc.operations.update.useMutation();


    // Set initial values from operation, if editing existing scrap close form
    useEffect(() => {
        if (operation.scrapReview) {
            setResult(operation.scrapReview.result);
            setComment(operation.scrapReview.comment);
            setOperator(operation.scrapReview.operator);
        }
    }, [operation]);

    const handleScrapReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setScrapReason(e.target.value as ScrapReason);
    }

    async function handleSubmit() {
        if (!formRef.current?.checkValidity()) {
            formRef.current!.reportValidity();
            return;
        }

        const releaseScrapLabel = result === 'release' || result === 'scrap';
        const createScrapLabel = result === 'inspect' &&
            operation.scrapReview != null && operation.scrapReview.result !== 'inspect';// Reobtain scrap label

        const updatedOperation = {
            ...operation,
            scrapReview: {
                result: result!,
                ...(result === 'scrap' && scrapReason ? { scrapReason } : {}),
                comment,
                operator,
                ...(operation.scrapReview?.time && { time: operation.scrapReview.time })
            },
        };
        if (createScrapLabel) {
            delete updatedOperation.scrapHandledAt;
        }

        try {
            const { operation } = await operationUpdate.mutateAsync({
                operation: updatedOperation,
                createScrapLabel,
                releaseScrapLabel,
            });
            onSuccess(operation);
            onClose();
        } catch (error) {
            reportError(error);
        }
    }


    const resultOptions = [
        { value: 'release', label: 'Release all' },
        { value: 'scrap', label: 'Scrap all' },
        { value: 'inspect', label: 'Inspect' },
    ];

    const operationDetails = operation.details.join(' > ');
    return <>
        <ModalSubmitDialog
            isOpen={isOpen}
            isSubmitting={operationUpdate.isPending}
            frameClassName='modal-frame--operation-dialog'
            contentClassName='modal-content--tablet'
            onSubmit={handleSubmit}
            onCancel={onClose}
        >
            <div className='operation-dialog__part_number'>{operation.partDisplayName}</div>
            <button style={{ float: 'right' }} onClick={() => setShowPdfDialog(true)}>
                View pdf
            </button>
            <div style={{ color: '#333' }}>
                <div>{operationDetails}</div>
                <div>Initial confirmed scraps {operation.confirmedScraps ?? 0}</div>
            </div>

            <form ref={formRef} style={{ marginTop: '20px' }}>
                <RadioGroup
                    header='Result'
                    name='result'
                    items={resultOptions}
                    value={result ?? ''}
                    required={true}
                    onChange={value => setResult(value as ScrapReviewResult)}
                />

                {result === 'scrap' && 
                    <fieldset
                        className={classNames('dialog-form__fieldset')}
                    >
                        <legend>Scrap reason</legend>
                        {ScrapReasons.map((scrapReasonOption) => (
                            (!scrapReasonOption.isHidden &&
                            <RadioButton
                                key={scrapReasonOption.label}
                                value={scrapReasonOption.label}
                                checked={scrapReasonOption.label === scrapReason}
                                onChange={handleScrapReasonChange}
                                required={true}
                            />
                        )))}
                    </fieldset>
                }

                <div className='dialog-form__field--with-margins'>
                    <div>Comment</div>
                    <textarea
                        className='dialog-form__input'
                        value={comment}
                        rows={2}
                        autoComplete='off'
                        onChange={(e) => setComment(e.target.value)}
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>Operator</div>
                    <OperatorSelect
                        className='dialog-form__input'
                        required={true}
                        operatorInitials={operator}
                        onChange={(operatorInitials) => setOperator(operatorInitials)}
                    />
                </div>
            </form>
        </ModalSubmitDialog >

        <ProtocolPreviewDialog
            product={operation.product}
            initialPositionIdx={null}
            show={showPdfDialog}
            onClose={() => setShowPdfDialog(false)}
        />
    </>
}

function RadioButton({
    value,
    checked,
    required,
    onChange,
}: {
    value: string,
    checked: boolean,
    required: boolean,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
}) {
    return (
        <label>
            <input
                className='radio--vert'
                type='radio'
                name='scrapReason'
                required={required}
                value={value}
                checked={checked}
                onChange={onChange}
            />
            {value}
        </label>
    )
}
